import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import { formatCurrency } from '../utils/formatters';
import CalendarWidget from '../components/CalendarWidget';
import GridLayout from 'react-grid-layout';
import '/node_modules/react-grid-layout/css/styles.css';
import '/node_modules/react-resizable/css/styles.css';
import './HomePage.css';

const HomePage = () => {
    const navigate = useNavigate();
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);


    // Widget States
    const [weather, setWeather] = useState(null);
    const [summary, setSummary] = useState({ hoje: [], vencidas: [] });
    const [quotationsAlert, setQuotationsAlert] = useState([]);
    const [notes, setNotes] = useState('');
    const [layout, setLayout] = useState([
        { i: 'weather', x: 0, y: 0, w: 4, h: 5, minW: 3, minH: 4 },
        { i: 'calendar', x: 0, y: 5, w: 4, h: 11, minW: 3, minH: 8 },
        { i: 'finance', x: 4, y: 0, w: 4, h: 11, minW: 4, minH: 8 },
        { i: 'notes', x: 4, y: 11, w: 4, h: 11, minW: 3, minH: 6 },
        { i: 'quotations', x: 0, y: 16, w: 4, h: 5, minW: 3, minH: 3 }
    ]);

    // Fetch User and Preferences
    useEffect(() => {
        const carregarDados = async () => {
            try {
                const userRes = await fetch('/api/auth/me', { credentials: 'include' });
                if (userRes.ok) {
                    const userData = await userRes.json();
                    setUser(userData.user);

                    // --- Layout Inicial Padrão ---
                    let initialLayout = [
                        { i: 'weather', x: 0, y: 0, w: 4, h: 5, minW: 3, minH: 4 },
                        { i: 'calendar', x: 0, y: 5, w: 4, h: 11, minW: 3, minH: 8 },
                        { i: 'finance', x: 4, y: 0, w: 4, h: 11, minW: 4, minH: 8 },
                        { i: 'notes', x: 4, y: 11, w: 4, h: 11, minW: 3, minH: 6 },
                        { i: 'quotations', x: 0, y: 16, w: 4, h: 5, minW: 3, minH: 3 }
                    ];

                    const prefRes = await fetch('/api/home-preferences', { credentials: 'include' });
                    if (prefRes.ok) {
                        const prefs = await prefRes.json();
                        if (prefs.homeLayout) {
                            try {
                                initialLayout = JSON.parse(prefs.homeLayout);
                            } catch (e) { console.error('Erro ao parsear layout salvo.'); }
                        }
                        if (prefs.homeNotes) setNotes(prefs.homeNotes);
                    }

                    // --- Filtragem por Permissões ---
                    if (!userData.user.isAdmin) {
                        const allowedTabs = userData.user.allowed_tabs ? JSON.parse(userData.user.allowed_tabs) : null;
                        const allowedWidgets = userData.user.allowed_widgets ? JSON.parse(userData.user.allowed_widgets) : null;

                        initialLayout = initialLayout.filter(w => {
                            // 1. Widget está explicitamente permitido?
                            if (allowedWidgets && !allowedWidgets.includes(w.i)) return false;

                            // 2. Se o widget depende de uma aba, a aba está permitida?
                            if (allowedTabs) {
                                if (w.i === 'calendar' && !allowedTabs.includes('calendario')) return false;
                                if (w.i === 'finance' && !allowedTabs.includes('financeiro')) return false;
                                if (w.i === 'quotations' && !allowedTabs.includes('cotacoes')) return false;
                            }

                            return true;
                        });
                    }

                    setLayout(initialLayout);
                } else {
                    navigate('/login');
                    return;
                }
            } catch (err) {
                console.error('Erro ao carregar dados iniciais:', err);
            } finally {
                setLoading(false);
            }
        };
        carregarDados();
    }, [navigate]);

    // Fetch Weather
    useEffect(() => {
        const fetchWeather = async () => {
            try {
                // Get location by IP
                let lat = -23.9608; // Santos
                let lon = -46.3339;
                let city = 'Santos, SP';

                try {
                    // Usando um serviço de IP mais simples e sem credenciais para evitar problemas de CORS/Preflight
                    const ipRes = await fetch('https://api.ipify.org?format=json');
                    if (ipRes.ok) {
                        const ipData = await ipRes.json();
                        // Opcional: buscar detalhes se necessário, ou usar o IP apenas.
                        // Para manter a lógica de lat/lon, tentamos o ipapi mas sem credentials.
                        const detailRes = await fetch(`https://ipapi.co/${ipData.ip}/json/`);
                        if (detailRes.ok) {
                            const detailData = await detailRes.json();
                            lat = detailData.latitude;
                            lon = detailData.longitude;
                            city = `${detailData.city}, ${detailData.region_code}`;
                        }
                    }
                } catch (e) { console.warn('Falha ao obter localização via IP, usando padrão.'); }

                const weatherRes = await fetch(`https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lon}&current_weather=true`);
                if (weatherRes.ok) {
                    const weatherData = await weatherRes.json();
                    setWeather({
                        temp: weatherData.current_weather.temperature,
                        code: weatherData.current_weather.weathercode,
                        city
                    });
                }
            } catch (err) {
                console.error('Erro ao buscar clima:', err);
            }
        };
        fetchWeather();
    }, []);

    // Fetch Finance Summary
    useEffect(() => {
        const fetchFinanceSummary = async () => {
            try {
                const res = await fetch(`/api/financas/resumo`, { credentials: 'include' });
                if (res.ok) {
                    const data = await res.json();
                    setSummary(data);
                }
            } catch (err) {
                console.error('Erro ao buscar resumo financeiro:', err);
            }
        };
        fetchFinanceSummary();
    }, []);

    // Fetch Quotations Alert
    useEffect(() => {
        const fetchQuotationsAlert = async () => {
            try {
                const res = await fetch('/api/cotacoes', { credentials: 'include' });
                if (res.ok) {
                    const data = await res.json();
                    setQuotationsAlert(data.filter(q => q.Status === 'Enviada'));
                }
            } catch (error) {
                console.error('Erro ao buscar alertas de cotação:', error);
            }
        };
        fetchQuotationsAlert();
    }, []);

    const savePreferences = useCallback(async (newLayout, newNotes) => {
        try {
            await fetch('/api/home-preferences', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ homeLayout: newLayout || layout, homeNotes: newNotes !== undefined ? newNotes : notes }),
                credentials: 'include'
            });
        } catch (err) {
            console.error('Erro ao salvar preferências:', err);
        }
    }, [layout, notes]);

    const onLayoutChange = (newLayout) => {
        setLayout(newLayout);
        savePreferences(newLayout, notes);
    };

    const handleNotesChange = (e) => {
        const val = e.target.value;
        setNotes(val);
        // Debounce or just save
    };

    const handleNotesBlur = () => {
        savePreferences(layout, notes);
    };

    const getWeatherDesc = (code) => {
        const codes = {
            0: 'Céu Limpo',
            1: 'Principalmente Limpo', 2: 'Parcialmente Nublado', 3: 'Nublado',
            45: 'Nevoeiro', 48: 'Nevoeiro com Gelo',
            51: 'Chuvisco Leve', 53: 'Chuvisco Moderado', 55: 'Chuvisco Denso',
            61: 'Chuva Leve', 63: 'Chuva Moderada', 65: 'Chuva Forte',
            80: 'Pancadas Leves', 81: 'Pancadas Moderadas', 82: 'Pancadas Fortes',
            95: 'Tempestade', 96: 'Tempestade com Granizo', 99: 'Tempestade Forte'
        };
        return codes[code] || 'Desconhecido';
    };

    if (loading) {
        return (
            <div className="loading-container" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100vh' }}>
                <p>Carregando...</p>
                <progress></progress>
            </div>
        );
    }


    return (
        <div className="homepage-container">
            <Navbar user={user} />
            <main className="main-content-wrapper">
                <GridLayout
                    className="layout"
                    layout={layout}
                    cols={12}
                    rowHeight={30}
                    width={1400}
                    onLayoutChange={onLayoutChange}
                    draggableHandle=".widget-header"
                >
                    <div key="weather" className="widget-container">
                        <div className="widget-classic">
                            <div className="widget-header">
                                <span>Clima Local</span>
                                <span className="icon">☁️</span>
                            </div>
                            <div className="widget-body weather-widget">
                                {weather ? (
                                    <>
                                        <div className="temp">{Math.round(weather.temp)}°C</div>
                                        <div className="city">{weather.city}</div>
                                        <div className="status">{getWeatherDesc(weather.code)}</div>
                                    </>
                                ) : <p>Buscando clima...</p>}
                            </div>
                        </div>
                    </div>

                    <div key="calendar" className="widget-container">
                        <CalendarWidget />
                    </div>
                    <div key="finance" className="widget-container">
                        <div className="widget-classic">
                            <div className="widget-header">
                                <span>Resumo Financeiro - Hoje</span>
                                <span className="icon">💰</span>
                            </div>
                            <div className="widget-body finance-widget">
                                <div className="summary-section">
                                    <h4 className="summary-title hoje">Vencem Hoje</h4>
                                    <div className="summary-list">
                                        {summary.hoje.length > 0 ? summary.hoje.map(t => (
                                            <div key={t.ID} className={`summary-item ${t.Tipo === 'Pagar' ? 'neg' : 'pos'}`}>
                                                <span className="desc">{t.Descricao}</span>
                                                <span className="val">{formatCurrency(t.Valor)}</span>
                                            </div>
                                        )) : <p className="empty-msg">Nenhuma conta para hoje.</p>}
                                    </div>
                                </div>

                                <div className="summary-section">
                                    <h4 className="summary-title atrasado">Pendentes Atrasados</h4>
                                    <div className="summary-list">
                                        {summary.vencidas.length > 0 ? summary.vencidas.map(t => (
                                            <div key={t.ID} className="summary-item overdue">
                                                <span className="desc">{t.Descricao}</span>
                                                <span className="val">{formatCurrency(t.Valor)}</span>
                                                <span className="date">
                                                    {(() => {
                                                        const d = new Date(t.DataVencimento);
                                                        const day = String(d.getUTCDate()).padStart(2, '0');
                                                        const month = String(d.getUTCMonth() + 1).padStart(2, '0');
                                                        const year = d.getUTCFullYear();
                                                        return `${day}/${month}/${year}`;
                                                    })()}
                                                </span>
                                            </div>
                                        )) : <p className="empty-msg">Sem pendências atrasadas.</p>}
                                    </div>
                                </div>

                                <div className="fin-footer">
                                    <button className="btn-classic" onClick={() => navigate('/financeiro')}>Ver Tudo</button>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div key="notes" className="widget-container">
                        <div className="widget-classic">
                            <div className="widget-header">
                                <span>Minhas Notas</span>
                                <span className="icon">📝</span>
                            </div>
                            <div className="widget-body notes-widget">
                                <textarea
                                    value={notes}
                                    onChange={handleNotesChange}
                                    onBlur={handleNotesBlur}
                                    placeholder="Escreva algo aqui..."
                                />
                            </div>
                        </div>
                    </div>

                    <div key="quotations" className="widget-container">
                        <div className="widget-classic">
                            <div className="widget-header">
                                <span>Cotações Recebidas</span>
                                <span className="icon">📩</span>
                            </div>
                            <div className="widget-body quotations-widget">
                                {quotationsAlert.length > 0 ? (
                                    <div className="alert-list">
                                        <p className="alert-text">Você tem <strong>{quotationsAlert.length}</strong> nova(s) cotação(ões)!</p>
                                        <button className="btn-classic primary" onClick={() => navigate('/cotacoes')}>Ver Todas</button>
                                    </div>
                                ) : (
                                    <p className="empty-msg">Nenhuma cotação nova pendente.</p>
                                )}
                            </div>
                        </div>
                    </div>
                </GridLayout>
            </main>
        </div>
    );
};

export default HomePage;
